import { toLonLat } from 'ol/src/proj';
import { getBottomLeft, getTopRight } from 'ol/src/extent';

import { fetchers, mapConfig, nameToIcon } from 'shared';

import getStationData, { operatorMappings, sharingMappings } from './sharing/getStationData';
import renderSharingTooltip from './renderSharingTooltip';
import { createMarker } from '../../utils';
import { renderSharingHoverTooltip, renderSharingSelectedTooltip } from './TooltipRenderer';

let sharingOperators;
let lastSettings;
let lastSelectedMarker = null;

function buildSharingOperators(settings, operators) {
	let result = [];
	Object.keys(settings).forEach(setting => {
		const operator = operators[operatorMappings[setting]];
		if (settings[setting] && operator) {
			result = result.concat(operator);
		}
	});
	return result;
}

/**
 * @param {import('ol/src/Map').default} map
 * @param {object} settings
 * @param {object} operators
 * @param {function} addPoint
 * @param {function} openPopup
 * @param {function} setSharingMarkers
 */
export default async function updateSharingMarkers(
	map,
	settings,
	operators,
	addPoint,
	openPopup,
	setSharingMarkers,
) {
	lastSettings = settings;
	sharingOperators = buildSharingOperators(settings, operators);

	const extent = map.getView().calculateExtent();

	if (sharingOperators.length === 0 || map.getView().getZoom() < mapConfig.minZoomLevel.sharing) {
		setSharingMarkers([]);
		return;
	}

	const bottomLeft = toLonLat(getBottomLeft(extent));
	const topRight = toLonLat(getTopRight(extent));

	const stations = await fetchers.fetchSharingStations(
		bottomLeft[1],
		bottomLeft[0],
		topRight[1],
		topRight[0],
		sharingOperators,
	);

	// sharingOperators could have changed, if the function was triggered again, so we re-check it.
	if (sharingOperators.length === 0 || stations === null) {
		setSharingMarkers([]);
		return;
	}

	const newSharingMarkers = [];

	stations.forEach(station => {
		// Skip stations that are no more in the current settings
		if (!lastSettings[sharingMappings[station.type] || station.type]) {
			return;
		}

		const stationData = getStationData(station);

		const marker = createMarker(station, {
			type: `sharing_${nameToIcon(sharingMappings[station.type] || station.type)}`,
			operator: sharingMappings[station.type] || station.type,
			vvsId: station.id,
			title: `${station.desc}, ${station.locality}`,
			locationtype: 'station',
			state: 2,
			onHover: () => {
				// Only show hover tooltip if not selected
				if (!marker.get('selected')) {
					renderSharingHoverTooltip(
						station,
						marker,
						map,
						(station, content, tooltipId, additionalContent) => {
							renderSharingTooltip(
								station,
								operators,
								content,
								additionalContent,
								openPopup,
							);
						},
					);
				}
			},
			onClick: () => {
				// Clear previous selection
				if (lastSelectedMarker !== null && lastSelectedMarker !== marker) {
					lastSelectedMarker.set('selected', false);
					lastSelectedMarker.changed();
				}
				
				// Mark as selected and show selected tooltip with button
				marker.set('selected', true);
				marker.changed();
				lastSelectedMarker = marker;
				
				renderSharingSelectedTooltip(
					station,
					marker,
					map,
					(stationPoint) => {
						addPoint({ ...stationData, coord: station.coords });
					},
					(station, content, tooltipId, additionalContent) => {
						renderSharingTooltip(
							station,
							operators,
							content,
							additionalContent,
							openPopup,
						);
					},
				);
			},
		});

		newSharingMarkers.push(marker);
	});

	setSharingMarkers(newSharingMarkers);
}
